import AnalysisMaCode from './ma/utils'
import AnalysisHibcCode from './hibc/utils'
import AnalysisHwCode from './hw/utils'
import AnalysisGs1Code from './gs1/parseGs1Utils'

import { getValueByPath as getValue, setValueByPath as setValue } from '../utils/objectUtil'
import protocolGs1Json from './gs1/protocol_gs1.json'

/** @name 解析protocolGs1Json */
const analysisGs1Json = (json) => {
  const m = new Map()
  const flags = Object.keys(json)
  for (const flag of flags) {
    const item = json[flag]
    if (!item) continue
    const name = getValue(item, 'mapping')
    if (name) m.set(name, { flag, name, item })
  }

  return m
}

/** @name 调用解析方法的映射表 */
const DecodeFunc = {
  gs1: new AnalysisGs1Code(), // GS1
  ma: AnalysisMaCode, // MA
  hibc: AnalysisHibcCode, // HIBC
  hw: AnalysisHwCode, // HW
}

DecodeFunc.gs1.init()
const jsonMap = analysisGs1Json(protocolGs1Json)

/** @name 判断条码是否只存在91字段 */
const ifOnly91 = (sequence) => {
  if (sequence?.length) {
    return !sequence.some((im) => im.flag === '21') // 如果找到了21字段，则代表只仅仅只有91字段
  }

  return false
}
/** @name 根据是否只存在91字段来更新sequence */
const updateSequenceFor91 = (sequence, result, vue) => {
  if (ifOnly91(sequence)) {
    const disabled91ToSerial = vue?.parentVue?.scanOptions?.disabled91ToSerial
    // console.log('disabled91ToSerial: ', disabled91ToSerial)
    sequence.forEach((sq) => {
      if (sq.flag === '91' && !disabled91ToSerial) {
        // 将91字段存储到21字段
        sq.flag = '21'
        sq.name = 'serialNumber'
        sq.value = `(${sq.flag})${sq.code}`

        // 替换更新 result.data.serialNumber 上的原厂序列号
        setValue(result, 'data.serialNumber', {
          ...getValue(result, 'data.custom'),
          label: '原厂序列号',
        })
      }
    })
  }
}

/** @name 生成ordinary */
const setOrdinary = (code, path = 'print.ordinary', result) => {
  let val
  if (/^gs1/gi.test(result.codeType)) {
    val = code.replace(/ñ/g, '')
  } else {
    val = code
  }
  if (val) setValue(result, path, val)
}
/** @name 生成bracket和section */
const setBracketAndSection = (code, path = 'print.bracket', result, field, vue) => {
  if (path === 'print.bracket') {
    const data = result?.data ?? {}
    const sequence = []
    // 遍历 data
    for (const name of Object.keys(data)) {
      const item = jsonMap.get(name)
      const flag = item?.flag
      const el = data[name]
      if (!el) continue
      const nIndex = +`${el.index}`
      if (flag && el.code && (nIndex || nIndex === 0)) {
        sequence.push({ index: nIndex, flag, name, code: el.code, value: `(${flag})${el.code}` })
      }
    }

    if (sequence.length > 0) {
      sequence.sort((a, b) => a.index - b.index) // 排序
      sequence.forEach((item) => {
        setValue(result, path, `${getValue(result, path) || ''}${item.value}`)
        // 递归生成 section
        if (item.flag) {
          setBracketAndSection(item.code, `print.section.${item.flag}`, result, item.name, vue)
        }
      })

      // 判断 21 和 91 是否同时存在，如果只有 91 单独存在则将 91 的值赋值到 (21)xxx
      updateSequenceFor91(sequence, result, vue)
      // console.log(sequence, 'sequence after update')
    }
  } else {
    if (code && path && field) {
      const item = jsonMap.get(field)
      const flag = item?.flag
      if (flag) {
        setValue(result, path, {
          name: field,
          ordinary: `${flag}${code}`,
          bracket: `(${flag})${code}`,
        })
      }
    }
  }
}

/** @name 各字段的标识 */
const __FLAG__ = {
  gtin: '01',
  batchNumber: '10',
  amount: '30',
  serialNumber: '21',
  periodBegin: '11',
  periodPack: '13',
  periodEnd: '17', // 本来是 12，但是把 periodExpire 和 periodEnd 显示为同一个标识 17
  periodExpire: '17',
  custom: '91',
}

/** @name 返回的数据结构 */
const result = {
  // 解析状态
  success: false,
  // 解析识别出的码类型
  codeType: null,
  // 扫码后获取到的值
  origin: '',
  // 扫码后获取到的打印值
  print: {
    ordinary: null, // 完整解析后适用于打印的条码（不带括号）
    bracket: null, // 完整解析后适用于打印的条码（带括号）
    /**
     * @description 解析后适用于打印的各分段条码
     * eg.
     * section: {
     *  10: {
     *    name: 'batchNumber',
     *    ordinary: 'yyxxx',
     *    bracket: '(yy)xxx'
     *  },
     *  21: {
     *    name: 'serialNumber',
     *    ordinary: 'yyxxx',
     *    bracket: '(yy)xxx'
     *  }
     *  ...
     * }
     */
    section: null,
  },
  /**
   * @description 解析结果
   * data = {
   *    nationCode:      '国家代码',
   *    enterpriseCode:  '企业代码',
   *    productIdent:    '产品标识',
   *    productCode:     '产品代码',
   *    batchNumber:     '批号',
   *    serialNumber:    '原厂序列号',
   *    periodBegin:     '生产日期',
   *    periodEnd:       '有效期至',
   *    periodExpire:    '失效日期',
   *    periodPack:      '包装日期',
   *    amount:          '可变数量',
   *    gtin:            'gtin',
   *    custom:          '自定义',
   *    hw:              '货位',
   * }
   */
  data: {},
}

// 更新识别后的码类型
const updateCodeType = (type) => {
  return type ? `${type}`.toUpperCase() + ' 码' : ''
}

export const parseScanCodeUtils = {
  /**
   * @name 重置result
   *
   */
  reset() {
    result.success = false
    result.codeType = null
    result.origin = ''
    result.print = {
      ordinary: null,
      bracket: null,
      section: null,
    }
    result.data = {}
  },
  /**
   * @name 扫码解析
   *
   * @param {string} code 扫码获取到的字符串
   * @param {string} [flows=['hibc', 'ma', 'gs1']] 解析流程 数组的值只能是 'hibc' | 'ma' | 'gs1'
   * @param {function} fnCallback 回调函数，参数为解析后结果 eg.(result) => void
   * @param {context} vue this
   * @return {*} 返回解析结果 result eg. { success: true, origin: code, codeType: 'gs1', data: {...} }
   */
  scan(code, flows = ['hibc', 'ma', 'gs1', 'hw'], fnCallback = null, vue) {
    try {
      if (!Array.isArray(flows)) throw new Error("第三个参数 flows 必须是数组，eg. ['hibc', 'ma']")
      // console.log('=============== 扫码 Scan ===============')
      // console.log('扫码的值: ', code)
      // console.log('扫码的值(长度): ', code?.length ?? 0)
      // console.log('待解析的值: ', code)
      // console.log('================== End ==================')

      this.reset()
      result.origin = code // 将原始存储至 result.origin

      for (let n = 0; n < flows.length; n++) {
        const flow = `${flows[n]}`.toLowerCase()
        const Fn = DecodeFunc[flow]

        if (!Fn) continue
        if (typeof flow !== 'string')
          throw new Error("第三个参数 flows 必须是字符串数组，eg. ['hibc', 'ma']")

        if (flow === 'hibc' || flow === 'ma') {
          // 解析 HIBC or MA 码
          try {
            const analysis = new Fn(code)
            const _data = analysis.map()
            // console.log('HIBC or MA 解析: ', _data)
            /* 赋值解析后的结果 */
            // if (!result.data.nationCode) result.data.nationCode = _data.nationCode || null; // 国家代码
            // if (!result.data.enterpriseCode) result.data.enterpriseCode = _data.enterpriseCode || null; // 企业代码
            // if (!result.data.productCode) result.data.productCode = _data.productCode || null; // 产品代码
            // ---------------------- 以下都会用到 ----------------------
            if (!result.data.productIdent) result.data.productIdent = _data.productIdent || null // 产品标识
            if (!result.data.batchNumber) result.data.batchNumber = _data.batchNumber || null // 批号
            if (!result.data.serialNumber) result.data.serialNumber = _data.serialNumber || null // 原厂序列号
            if (!result.data.periodBegin) result.data.periodBegin = _data.periodBegin || null // 生产日期
            if (!result.data.periodEnd) result.data.periodEnd = _data.periodEnd || null // 有效期至
            if (!result.data.periodExpire) result.data.periodExpire = _data.periodExpire || null // 失效日期
            if (!result.data.amount) result.data.amount = _data.amount || null // 数量

            result.codeType = updateCodeType(flow)
            // 生成ordinary
            setOrdinary(result.origin, 'print.ordinary', result)
            break // 识别后则终止识别
          } catch (error) {
            // console.warn(`解析 ${`${flow}`.toUpperCase()} 码未成功，将尝试其他解析方法...`)
            result.codeType = updateCodeType()
          }
        } else if (flow === 'gs1') {
          const analysis = Fn
          // 解析 GS1 码
          const _data = analysis.variedKey(analysis.decode(code), true)
          // console.log('GS1 解析: ', _data)
          /* 赋值解析后的结果 */
          // GS1
          if (!result.data.gtin) {
            result.data.gtin = _data.gtin
              ? { label: 'gtin', code: _data.gtin, value: _data.gtin, index: 0 }
              : null
          }
          if (!result.data.custom) {
            result.data.custom = _data.custom
              ? {
                  label: '自定义',
                  code: _data.custom,
                  value: _data.custom,
                  index: code.indexOf(__FLAG__.custom + _data.custom),
                }
              : null
          }
          if (!result.data.batchNumber) {
            result.data.batchNumber = _data.batchNumber
              ? {
                  label: '批号',
                  code: _data.batchNumber,
                  value: _data.batchNumber,
                  index: code.indexOf(__FLAG__.batchNumber + _data.batchNumber),
                }
              : null
          }
          // 根据需求#5700修改，优先生产日期、如果生产日期未扫到，则将包装日期赋值给生产日期
          if (!result.data.periodBegin) {
            result.data.periodBegin =
              _data.periodBegin?.date || _data.periodPack?.date
                ? {
                    label: '生产日期',
                    code: _data.periodBegin?.value || _data.periodPack?.value,
                    value: _data.periodBegin?.date || _data.periodPack?.date,
                    index: code.indexOf(
                      __FLAG__.periodBegin + _data.periodBegin?.value ||
                        __FLAG__.periodPack + _data.periodPack?.value
                    ),
                  }
                : null
          }
          if (!result.data.periodEnd) {
            result.data.periodEnd = _data.periodEnd?.date
              ? {
                  label: '有效期至',
                  code: _data.periodEnd?.value,
                  value: _data.periodEnd?.date,
                  index: code.indexOf(__FLAG__.periodEnd + _data.periodEnd?.value),
                }
              : null
          }
          if (!result.data.periodExpire) {
            result.data.periodExpire = _data.periodExpire?.date
              ? {
                  label: '失效日期',
                  code: _data.periodExpire?.value,
                  value: _data.periodExpire?.date,
                  index: code.indexOf(__FLAG__.periodExpire + _data.periodExpire?.value),
                }
              : null
          }
          if (!result.data.periodPack) {
            result.data.periodPack = _data.periodPack?.date
              ? {
                  label: '包装日期',
                  code: _data.periodPack?.value,
                  value: _data.periodPack?.date,
                  index: code.indexOf(__FLAG__.periodPack + _data.periodPack?.value),
                }
              : null
          }
          if (!result.data.serialNumber) {
            result.data.serialNumber = _data.serialNumber
              ? {
                  label: '原厂序列号',
                  code: _data.serialNumber,
                  value: _data.serialNumber,
                  index: code.indexOf(__FLAG__.serialNumber + _data.serialNumber),
                }
              : null
          }
          if (!result.data.amount) {
            result.data.amount = _data.amount
              ? {
                  label: '可变数量',
                  code: _data.amount,
                  value: _data.amount,
                  index: code.indexOf(__FLAG__.amount + _data.amount),
                }
              : null
          }

          // 生成ordinary
          setOrdinary(result.origin, 'print.ordinary', result)
          // 生成 bracket 和 section
          setBracketAndSection(result.origin, 'print.bracket', result, null, vue)

          // 判断 GS1 是否解析成功
          if (_data.error) {
            // console.warn(`解析 GS1 码未成功，提示信息: ${_data.error}。将尝试其他解析方法...`)
            result.codeType = updateCodeType()
          } else {
            result.codeType = updateCodeType(flow)
            break // 识别后则终止识别
          }
        } else if (flow === 'hw') {
          // 解析货位
          try {
            const analysis = new Fn(code)
            const _data = analysis.map()
            // console.log('货位条码解析: ', _data)
            /* 赋值解析后的结果 */
            if (!result.data.hw) result.data.hw = _data.hw || null

            result.codeType = updateCodeType(flow)
            break // 识别后则终止识别
          } catch (error) {
            // console.warn(`解析货位未成功，将尝试其他解析方法...`)
            result.codeType = updateCodeType()
          }
        }
      }
    } catch (error) {
      console.error('解析码报错: ', error.message)
    }

    // 根据扫码解析结果赋值
    result.success = this.isSuccess(result.data)
    // 扫码解析不成功，将 codeType 设为空字符串
    // if (!result.success) result.codeType = updateCodeType()

    // console.log('=============== 打印 Print ===============')
    // console.log('解析后结果: ', result)
    Object.keys(result.data).forEach((key) => {
      const { label, value } = result.data[key] || {}
      if (label) console.log(`${label}: ${value || ''}`)
    })
    // console.log('================== End ==================')

    // 回调抛出解析后结果
    if (fnCallback) fnCallback(result)
    return result
  },
  /**
   * @name 判断扫码结果是否解析成功
   *
   * @param {object} [data={}] 扫码解析的结果数据 result.data
   * @return {boolean}
   */
  isSuccess(data = {}) {
    // console.log('扫码结果(pangu-core) data: ', data)
    const ret = Object.keys(data).reduce((total, key) => {
      const current = data[key] || {}
      if (current.value) total.push(current.value)
      return total
    }, [])

    let success = ret.length > 0
    // 即使条码识别为 GS1，但解析的属性不在可识别的范围下（即 ret 为 []），则将 codeType 设为空字符串
    if (!success) result.codeType = updateCodeType()
    if (!result.codeType) success = false // result.codeType 可能在函数 isSuccess 外已赋值，所以这里再多判断一次

    return success
  },
}
